// Header Media Uploader:
// Uploads file directly to Firebase Storage (`tenants/{t}/templateMedia/{fileId}`),
// then calls `whatsappUploadTemplateMedia` callable to stream to Meta Resumable Upload API
// and obtain `header_handle`.

import { useRef, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { useTenant } from '../../lib/tenant';
import { uploadTemplateMedia } from '../../lib/whatsappApi';
import { useI18n } from '../../lib/i18n';
import type { WaTemplateHeaderMedia } from '../../lib/templateTypes';

export interface MediaUploaderProps {
  mediaType: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  value: WaTemplateHeaderMedia | null;
  onChange: (media: WaTemplateHeaderMedia | null, localPreviewUrl?: string | null, fileName?: string | null) => void;
  integrationId?: string;
  disabled?: boolean;
}

const MAX_IMAGE_VIDEO_BYTES = 16 * 1024 * 1024; // 16 MB
const MAX_DOCUMENT_BYTES = 100 * 1024 * 1024; // 100 MB

export default function MediaUploader({
  mediaType,
  value,
  onChange,
  integrationId,
  disabled = false,
}: MediaUploaderProps) {
  const { t } = useI18n();
  const { tenantId } = useTenant();
  const inputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const accept =
    mediaType === 'IMAGE'
      ? 'image/jpeg,image/png'
      : mediaType === 'VIDEO'
      ? 'video/mp4'
      : 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain';

  const maxBytes = mediaType === 'DOCUMENT' ? MAX_DOCUMENT_BYTES : MAX_IMAGE_VIDEO_BYTES;
  const maxMb = mediaType === 'DOCUMENT' ? '100 MB' : '16 MB';

  async function handleFileSelected(file: File) {
    if (!tenantId) {
      setError(t('tpl.media.noTenant') || 'Tenant context required');
      return;
    }

    if (file.size > maxBytes) {
      setError(t('tpl.media.tooLarge') || `File exceeds maximum size of ${maxMb}`);
      return;
    }

    // The <input accept> filter is skipped by drag-and-drop, so check the
    // real type: a PNG uploaded as a VIDEO header is accepted by Storage and
    // only fails later at Meta (131053) — after the campaign was "sent".
    const allowed = accept.split(',');
    if (!file.type || !allowed.includes(file.type)) {
      setError(t('tpl.media.wrongType', { type: file.type || file.name.split('.').pop() || '?', expected: accept.replace(/,/g, ', ') }));
      return;
    }

    setError(null);
    setUploading(true);
    setProgress(0);
    setFileName(file.name);

    // Create a local blob preview URL for immediate responsive feedback
    let localUrl: string | null = null;
    if (mediaType === 'IMAGE') {
      localUrl = URL.createObjectURL(file);
    }

    try {
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileId = `${Date.now()}_${sanitizedName}`;
      const storagePath = `tenants/${tenantId}/templateMedia/${fileId}`;
      const storageRef = ref(storage, storagePath);

      // Step 1: Upload to Firebase Storage
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
      });

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 70);
            setProgress(pct);
          },
          (err) => reject(err),
          () => resolve(),
        );
      });

      // Get download URL if possible
      let downloadUrl = localUrl;
      try {
        downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
      } catch {
        // Fallback to localUrl
      }

      setProgress(80);

      // Step 2: Stream from Storage to Meta Resumable Upload API via Cloud Function
      const metaRes = await uploadTemplateMedia({
        storagePath,
        mimeType: file.type || (mediaType === 'IMAGE' ? 'image/jpeg' : mediaType === 'VIDEO' ? 'video/mp4' : 'application/pdf'),
        fileSize: file.size,
        integrationId: integrationId || undefined,
      });

      setProgress(100);

      const headerMedia: WaTemplateHeaderMedia = {
        handle: metaRes.header_handle || metaRes.handle,
        mime: metaRes.mime || file.type,
        size: metaRes.size || file.size,
        storagePath,
        createdAt: metaRes.createdAt || Date.now(),
      };

      onChange(headerMedia, downloadUrl, file.name);
    } catch (err) {
      setError((err as Error).message || 'Failed to upload media');
      onChange(null, null, null);
    } finally {
      setUploading(false);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    if (disabled || uploading) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      void handleFileSelected(e.dataTransfer.files[0]);
    }
  }

  function handleRemove() {
    setError(null);
    setFileName(null);
    onChange(null, null, null);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled || uploading}
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            void handleFileSelected(e.target.files[0]);
          }
        }}
      />

      {value ? (
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-100 text-sm font-bold text-emerald-700">
              ✓
            </span>
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-slate-800">
                {fileName || `${mediaType.toLowerCase()}_sample`}
              </p>
              <p className="truncate text-[10px] text-slate-500 font-mono">
                Handle: {value.handle.slice(0, 18)}...
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t('tpl.media.replace') || 'Replace'}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled || uploading}
              className="rounded-lg border border-red-200 bg-white px-2 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50"
            >
              ✕
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => !uploading && !disabled && inputRef.current?.click()}
          className={`group flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-4 text-center transition-all ${
            uploading
              ? 'border-indigo-400 bg-indigo-50/50'
              : 'border-slate-300 bg-slate-50/60 hover:border-indigo-400 hover:bg-indigo-50/30'
          }`}
        >
          {uploading ? (
            <div className="w-full max-w-[200px] space-y-2 py-2">
              <div className="flex justify-between text-[11px] font-semibold text-indigo-700">
                <span>{progress < 75 ? 'Uploading...' : 'Processing with Meta...'}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-indigo-100">
                <div
                  className="h-full bg-indigo-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="grid h-10 w-10 place-items-center rounded-full bg-white shadow-sm text-slate-600 group-hover:scale-110 transition-transform">
                {mediaType === 'IMAGE' ? '🖼️' : mediaType === 'VIDEO' ? '🎥' : '📄'}
              </div>
              <p className="mt-2 text-xs font-semibold text-slate-800">
                {t('tpl.media.drop') || `Click or drag ${mediaType.toLowerCase()} to upload`}
              </p>
              <p className="mt-0.5 text-[10px] text-slate-400">
                {t('tpl.media.limits') || `Max ${maxMb} · Required by Meta for sample review`}
              </p>
            </>
          )}
        </div>
      )}

      {error && (
        <p className="text-[11px] font-medium text-red-600">
          ⚠️ {error}
        </p>
      )}
    </div>
  );
}
